import api from '@pray/shared/utils/api';

import type {
  SuperFunnelAccountStatus,
  SuperFunnelArStatus,
  SuperFunnelContractStatus,
  SuperFunnelProductType,
} from './getAccounts';

export type SuperFunnelContractArtist = {
  id: string;
  name: string | null;
};

export type SuperFunnelContractOrganization = {
  id: string;
  name: string | null;
  legal_entity_name: string | null;
  hubspot_company_id: string | null;
};

export type SuperFunnelContractListItem = {
  id: string;
  name: string | null;
  product_type: SuperFunnelProductType | null;
  types?: SuperFunnelProductType[];
  status: SuperFunnelContractStatus | null;
  exception_flag: boolean;
  contract_date: string | null;
  service_start_date: string | null;
  billing_start_date: string | null;
  cancel_date: string | null;
  contract_term_months: number | null;
  target_spend_pct: string | null;
  total_budget_cents: number | null;
  contract_value_cents: number | null;
  // Delivered spend across the contract's campaigns, in cents. total_spend_cents is Adjusted
  // (credit-net) retail — the existing progress-bar basis. The three twins carry the rest of the
  // W/R x Adjusted/Actual matrix (PB-37829); optional so a pre-deploy server degrades gracefully.
  total_spend_cents: number;
  total_spend_wholesale_cents?: number;
  total_spend_actual_cents?: number;
  total_spend_wholesale_actual_cents?: number;
  service_fee_percent: string | null;
  hubspot_deal_id: string | null;
  created_at: string | null;
  // null when the contract's artist can't be resolved (no marketing campaign /
  // null artist_id). The server sends `artist: null` for these; the FE renders
  // them without a leader name (falls back to org name / "—").
  artist: SuperFunnelContractArtist | null;
  // Orphan contracts (Artist.organization_id IS NULL) surface with
  // organization: null. FE renders these as "Unassigned" so the operator
  // can fix in Studio.
  organization: SuperFunnelContractOrganization | null;
  latest_ar_snapshot: {
    ar_status: SuperFunnelArStatus | string;
    outstanding_over_30_cents: number;
  } | null;
};

export type GetSuperFunnelContractsListParams = {
  ministry?: string;
  productType?: SuperFunnelProductType;
  contractStatus?: SuperFunnelContractStatus;
  accountStatus?: SuperFunnelAccountStatus;
  arStatus?: SuperFunnelArStatus;
  exceptionFlag?: boolean;
  limit?: number;
  offset?: number;
};

export type GetSuperFunnelContractsListResponse = {
  responded_at: string;
  object: 'list';
  data: SuperFunnelContractListItem[];
  data_count: number;
  last_item_identifier: number | null;
  total: number;
  limit: number;
  offset: number;
};

export const getContractsList = async ({
  ministry,
  productType,
  contractStatus,
  accountStatus,
  arStatus,
  exceptionFlag,
  limit,
  offset,
}: GetSuperFunnelContractsListParams = {}): Promise<GetSuperFunnelContractsListResponse> => {
  const params: Record<string, string | number | boolean> = {};

  if (ministry !== undefined) params.ministry = ministry;
  if (productType !== undefined) params.product_type = productType;
  if (contractStatus !== undefined) params.contract_status = contractStatus;
  if (accountStatus !== undefined) params.account_status = accountStatus;
  if (arStatus !== undefined) params.ar_status = arStatus;
  if (exceptionFlag !== undefined) params.exception_flag = exceptionFlag;
  if (limit !== undefined) params.limit = limit;
  if (offset !== undefined) params.offset = offset;

  const url = '/web/studio/superfunnel/contracts';
  const response = await api.executeGetRequest(url, params);

  return response.data as GetSuperFunnelContractsListResponse;
};
